/**
 * Event bridge for a workspace that is gone or scheduled for deletion.
 *
 * Contract:
 *   - `api.service` interceptor emits `emitWorkspaceUnavailable(workspaceId)`
 *     on a 403 with code `WORKSPACE_UNAVAILABLE` (the backend's answer for a
 *     deleted workspace, or one its owner scheduled for deletion), naming the
 *     workspace the request was sent for.
 *   - `WorkspaceContext` subscribes and, when that is the active workspace,
 *     refreshes the user's workspace list so they are moved to one they still
 *     have (or, with none left, to the "no workspace" state).
 *
 * SSR-safe: any environment without `window` no-ops.
 */

const EVENT_NAME = 'bower:workspace-unavailable';

export interface WorkspaceUnavailableDetail {
  /** The `x-workspace-id` the refused request carried, if any. */
  readonly workspaceId: string | null;
}

export function emitWorkspaceUnavailable(workspaceId: string | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<WorkspaceUnavailableDetail>(EVENT_NAME, { detail: { workspaceId } }),
  );
}

/** Subscribe; returns a cleanup function for React `useEffect`. */
export function subscribeWorkspaceUnavailable(
  handler: (detail: WorkspaceUnavailableDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event) => {
    handler((event as CustomEvent<WorkspaceUnavailableDetail>).detail);
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
